// src/pages/production/MaterialConsumptionPage.tsx

import { useEffect, useState } from "react";
import { SmartEntryModal } from "@/features/production/components/SmartEntryModal";
import { MaterialBalanceIndicator } from "@/features/production/components/MaterialBalanceIndicator";
import { EntityPicker } from "@/shared/components/EntityPicker";
import { PageBanner } from "@/shared/components/PageBanner";
import { usePermission } from "@/auth/usePermission";
import { useV1ProductionScrapReasonCodesList } from "@/api/generated/v1/v1";
import {
  useSubmitUnifiedConsumptionAction,
  useBackflushSuggestions,
  useMaterialBalance,
  type BackflushSuggestion,
  type MaterialBalanceResult,
} from "@/api/unifiedConsumption";

export function MaterialConsumptionPage() {
  const [workOrder, setWorkOrder] = useState<{ id: string; display: string } | null>(null);
  const workOrderId = workOrder?.id || null;
  const [actualProducedQuantity, setActualProducedQuantity] = useState("");
  const [suggestions, setSuggestions] = useState<BackflushSuggestion[]>([]);
  const [activeLine, setActiveLine] = useState<string | null>(null);
  const [balance, setBalance] = useState<MaterialBalanceResult | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(false);

  const currentUserHasQcPermission = usePermission("inventory.manage");

  const { data: scrapReasonsData } = useV1ProductionScrapReasonCodesList(undefined, {
    query: {
      staleTime: Infinity,
    },
  });

  const scrapReasonOptions = (scrapReasonsData?.results || []).map((r) => ({
    id: r.id,
    label: r.description || r.code,
  }));

  const submitAction = useSubmitUnifiedConsumptionAction();
  const backflush = useBackflushSuggestions(workOrderId || null, actualProducedQuantity || null);
  const balanceQuery = useMaterialBalance({ work_order: workOrderId || null });

  async function loadBackflushSuggestions() {
    if (!backflush.enabled) return;
    const result = await backflush.fetcher();
    if (result.data) {
      setSuggestions(result.data);
    }
  }

  async function refreshBalance() {
    if (!workOrderId) return;
    setBalanceLoading(true);
    try {
      const result = await balanceQuery.fetcher();
      if (result.data) {
        setBalance(result.data);
      }
    } finally {
      setBalanceLoading(false);
    }
  }

  useEffect(() => {
    if (workOrderId) {
      refreshBalance();
    } else {
      setBalance(null);
      setSuggestions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workOrderId]);

  return (
    <div
      dir="rtl"
      className="am033-consumption-page"
      style={{
        padding: "0.5rem 0",
        maxWidth: "100%",
        margin: "0",
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
        fontFamily: "var(--font-family-fa, sans-serif)",
      }}
    >
      <PageBanner
        title="مصرف یکپارچه مواد (Material Consumption)"
        subtitle="ثبت هوشمند مصرف پالت‌ها، ضایعات پای خط و محاسبه آنلاین بالانس مواد"
        icon="⚡"
        helpLink="/help/production/consumption"
      >
        <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", alignItems: "center" }}>
          {workOrderId && (
            <span
              style={{
                padding: "0.4rem 1rem",
                borderRadius: "12px",
                background: "rgba(255, 255, 255, 0.2)",
                color: "#FFFFFF",
                fontSize: "0.85rem",
                fontWeight: 600,
              }}
            >
              سفارش فعال: {workOrder?.display || workOrderId}
            </span>
          )}
        </div>
      </PageBanner>

      {/* SELECT WORK ORDER CARD */}
      <div className="brand-card-extand" style={{ padding: "1.5rem" }}>
        <h3
          style={{
            margin: "0 0 1rem 0",
            fontSize: "1.05rem",
            fontWeight: 800,
            color: "#007270",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
          }}
        >
          <span>🎯</span>
          <span>انتخاب سفارش تولید (Work Order)</span>
        </h3>
        <div style={{ maxWidth: "550px" }}>
          <label
            style={{
              fontSize: "0.85rem",
              fontWeight: 700,
              color: "#2B2B2B",
              display: "block",
              marginBottom: "8px",
            }}
          >
            سفارش تولید را جهت بارگذاری فرمول ساخت و کنترل بالانس انتخاب نمایید:
          </label>
          <EntityPicker
            model="production.workorder"
            value={workOrder}
            onChange={setWorkOrder}
            placeholder="جستجو و انتخاب سفارش تولید..."
          />
        </div>
      </div>

      {workOrderId && (
        <>
          {/* BALANCE INDICATOR */}
          <MaterialBalanceIndicator balance={balance} loading={balanceLoading} />

          {/* BACKFLUSH SUGGESTION CALCULATOR */}
          <div className="brand-card-extand" style={{ padding: "1.5rem" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "1.25rem",
              }}
            >
              <div>
                <h3
                  style={{
                    margin: "0 0 0.35rem 0",
                    fontSize: "1.05rem",
                    fontWeight: 800,
                    color: "#007270",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <span>⚡</span>
                  <span>محاسبه پیشنهاد مصرف بر اساس تیراژ واقعی (Backflush)</span>
                </h3>
                <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--color-text-muted, #666)" }}>
                  با وارد کردن تیراژ تولیدشده، اقلام BOM مصرفی متناظر بر اساس فرمول ساخت به طور خودکار پیشنهاد می‌شوند.
                </p>
              </div>

              <div
                className="am033-consumption-page__backflush"
                style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}
              >
                <label
                  style={{
                    fontSize: "0.85rem",
                    fontWeight: 700,
                    color: "#2B2B2B",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <span>مقدار تولیدشده:</span>
                  <input
                    type="number"
                    value={actualProducedQuantity}
                    onChange={(e) => setActualProducedQuantity(e.target.value)}
                    data-testid="actual-produced-quantity-input"
                    placeholder="مثلاً 100"
                    style={{
                      padding: "8px 12px",
                      borderRadius: "10px",
                      border: "1px solid var(--color-border, #E4DCC8)",
                      background: "#FFFFFF",
                      color: "#2B2B2B",
                      fontSize: "0.9rem",
                      outline: "none",
                      width: "130px",
                    }}
                  />
                </label>
                <button
                  type="button"
                  onClick={loadBackflushSuggestions}
                  data-testid="backflush-button"
                  disabled={!actualProducedQuantity}
                  style={{
                    padding: "8px 20px",
                    borderRadius: "10px",
                    background: !actualProducedQuantity ? "#E5E7EB" : "#007270",
                    color: !actualProducedQuantity ? "#9CA3AF" : "#FFFFFF",
                    border: "none",
                    fontWeight: 700,
                    fontSize: "0.85rem",
                    cursor: !actualProducedQuantity ? "not-allowed" : "pointer",
                    boxShadow: !actualProducedQuantity ? "none" : "0 4px 10px rgba(0, 114, 112, 0.25)",
                    transition: "all 0.2s ease",
                  }}
                >
                  محاسبه پیشنهاد مصرف
                </button>
              </div>
            </div>
          </div>

          {/* SUGGESTIONS TABLE */}
          {suggestions.length > 0 && (
            <div className="brand-card-extand" style={{ padding: "0", overflow: "hidden" }}>
              <div
                style={{
                  padding: "1rem 1.25rem",
                  borderBottom: "1px solid var(--color-border, #E4DCC8)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 800, color: "#2B2B2B" }}>
                  📋 اقلام فرمول ساخت و مقادیر پیشنهادی (BOM Items)
                </h3>
                <span style={{ fontSize: "0.85rem", color: "var(--color-text-muted, #666)" }}>
                  تعداد اقلام: {suggestions.length}
                </span>
              </div>
              <div style={{ overflowX: "auto" }}>
                <table
                  className="am033-consumption-page__suggestions"
                  data-testid="suggestions-table"
                  style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}
                >
                  <thead>
                    <tr
                      style={{
                        background: "rgba(0,0,0,0.02)",
                        borderBottom: "1px solid var(--color-border, #E4DCC8)",
                        color: "var(--color-text-main, #2B2B2B)",
                      }}
                    >
                      <th style={{ padding: "1rem", textAlign: "right", fontWeight: 800 }}>قلم فرمول ساخت (BOM)</th>
                      <th style={{ padding: "1rem", textAlign: "center", fontWeight: 800 }}>کل مقدار مورد انتظار</th>
                      <th style={{ padding: "1rem", textAlign: "center", fontWeight: 800 }}>قبلاً مصرف‌شده</th>
                      <th style={{ padding: "1rem", textAlign: "center", fontWeight: 800 }}>پیشنهاد مصرف فعلی</th>
                      <th style={{ padding: "1rem", textAlign: "center", fontWeight: 800 }}>عملیات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {suggestions.map((s) => (
                      <tr
                        key={s.bom_item}
                        style={{ borderBottom: "1px solid rgba(0,0,0,0.05)", transition: "background 0.2s" }}
                        className="hover:bg-gray-50/50"
                      >
                        <td style={{ padding: "1rem", fontWeight: 700, color: "#007270" }}>{s.bom_item}</td>
                        <td style={{ padding: "1rem", textAlign: "center", fontWeight: 600 }}>
                          {s.expected_total_quantity}
                        </td>
                        <td style={{ padding: "1rem", textAlign: "center", fontWeight: 600, color: "#666" }}>
                          {s.already_consumed_quantity}
                        </td>
                        <td style={{ padding: "1rem", textAlign: "center" }}>
                          <span
                            style={{
                              background: "rgba(16, 185, 129, 0.15)",
                              color: "#10B981",
                              padding: "4px 12px",
                              borderRadius: "6px",
                              fontWeight: 700,
                              fontSize: "0.85rem",
                            }}
                          >
                            {s.suggested_quantity}
                          </span>
                        </td>
                        <td style={{ padding: "1rem", textAlign: "center" }}>
                          <button
                            type="button"
                            onClick={() => setActiveLine(s.bom_item)}
                            data-testid={`open-entry-${s.bom_item}`}
                            style={{
                              padding: "6px 16px",
                              borderRadius: "8px",
                              background: "#007270",
                              color: "#ffffff",
                              border: "none",
                              fontWeight: 700,
                              fontSize: "0.8rem",
                              cursor: "pointer",
                              boxShadow: "0 2px 8px rgba(0, 114, 112, 0.2)",
                              transition: "background 0.2s",
                            }}
                          >
                            ثبت مصرف پالت
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* SMART ENTRY MODAL */}
      {activeLine && (
        <SmartEntryModal
          bomItemId={activeLine}
          scrapReasonOptions={scrapReasonOptions}
          currentUserHasQcPermission={currentUserHasQcPermission}
          onCancel={() => setActiveLine(null)}
          onSubmit={async (line) => {
            if (!workOrderId) return;
            await submitAction.mutateAsync({ work_order: workOrderId, lines: [line] });
            setActiveLine(null);
            await refreshBalance();
          }}
        />
      )}
    </div>
  );
}

export default MaterialConsumptionPage;
